/**
 * This function helps to format the name of the section/operation/run, or use it's index, if it hasn't a name
 * @param name
 * @param idx
 * @returns
 */

const formatName = (name: string, idx: number): string => (name ? ' - ' + name : ' (' + (idx + 1) + ')')

/**
 * This functions turns the received tree into an object that can be used to build the gantt chart
 * @param tree
 * @returns
 */

export const turnIntoGanttSeries = (tree: UnformattedTreeProps): Array<formattedGanttSeries> => {
    let res: Array<formattedGanttSeries> = []
    let y = 0
    if (tree.interventions.length > 0) {
        tree.interventions.map((e, i) => {
            let interventionKey = e.id + '_' + i
            res.push({
                name: 'Intervention ' + e.id,
                id: interventionKey,
                y: y
            })
            e.sections.map((f, j) => {
                let sectionKey = i + '_' + j
                res.push({
                    name: 'Section' + formatName(f.name, f.index),
                    id: sectionKey,
                    parent: interventionKey,
                    y: y++
                })
                f.runs.map((g, k) => {
                    let runKey = i + '_' + j + '_' + k
                    res.push({
                        name: 'Run' + formatName(g.name, k),
                        id: runKey,
                        parent: sectionKey,
                        y: y++,
                        collapsed: true
                    })
                    if (g?.operations?.length > 0)
                        g.operations.forEach((operation, l) => {
                            let operationKey = i + '_' + j + '_' + k + '_' + l
                            res.push(formatOperations(operation, operationKey, runKey, y))
                        })
                    if (g?.casingOperations?.length > 0)
                        g.casingOperations.forEach((operation, l) => {
                            let operationKey = i + '_' + j + '_' + k + '_' + l
                            res.push(formatOperations(operation, operationKey, runKey, y))
                        })
                })
            })
        })
    }
    return res
}

/**
 * This function receives an operation and returns an object ready to be added
 * on a structure that will be used on the gantt.
 * @param operation
 * @param operationKey
 * @param runKey
 * @param y
 * @returns
 */

const formatOperations = (
    operation: TreeOperation,
    operationKey: string,
    runKey: string,
    y: number
): formattedGanttSeries => {
    let res: formattedGanttSeries = {
        name: operation.name,
        id: operationKey,
        parent: runKey,
        start: setDate(operation.start_timestamp),
        end: setDate(operation.end_timestamp),
        DateStartDisplay: setDate(operation.start_timestamp).toString(),
        DateEndDisplay: setDate(operation.end_timestamp).toString(),
        y: y++
    }
    return res
}

/**
 * This function helps to properly format the date
 * @param date
 * @returns
 */

export const setDate = (date: string | number): number => {
    let day = new Date(date)
    return Date.UTC(
        day.getFullYear(),
        day.getMonth() + 1,
        day.getDate(),
        day.getHours(),
        day.getMinutes(),
        day.getSeconds()
    )
}

/**
 * This function gets the tree from the backend
 * @param assetName {String} - the selected asset
 * @param loadingSelectedUnitSet {boolean} - informantion about loading the tree
 * @param selectedUnitSet {id: boolean} - the unitset chosen in live
 * @returns {Promise} - when the fetching part is done it returns the tree or the error
 */

export const getTreeUnformatted = (
    assetName: string,
    setFetchingTree: Function,
    setError: Function
) => {
    setFetchingTree(true)
    setError(false)
    return fetch(
        `/services/plugin-drilling-operations-span-picker/tree/${encodeURIComponent(
            assetName
        )}/plugin-drilling-operations-span-picker`
    )
        .then(r => {
            setFetchingTree(false)
            return r.json()
        })
        .catch(error => {
            setFetchingTree(false)
            setError(true)
            console.log('Error on fetching tree: ', error)
            return Promise.reject()
        })
}

/**
 * This function gets the asset name from the lookup asset_name_event_type (usually passed by the dashboard context)
 * @param filter {String} - a stringified version of the dashboard filters' object
 * @returns {String} - the name of the first asset on the lookup asset_name_event_type
 */

export const getAssetName = (filter: string, assetThroughWidgetContext): string => {
    let res = ''
    const lookup = JSON.parse(filter || '{}')
    if (lookup['asset_name_event_type']) {
        res = lookup['asset_name_event_type'][0]
    } else if (assetThroughWidgetContext?.length > 0) {
        res = assetThroughWidgetContext[0]
    }
    return res
}
